/**
 * cognitoSession — inyección de una sesión Cognito obtenida fuera de Amplify.
 *
 * NER-3781 / NER-3807: el flujo Google admin no autentica vía Amplify (`signIn`),
 * sino que el backend (`POST /social/complete-signup`) crea el usuario
 * Cognito y devuelve los tokens ya emitidos (`idToken`/`accessToken`/`refreshToken`).
 * Amplify v6 NO expone una API pública tipo `Amplify.setSession(tokens)`, así que
 * persistimos esos tokens en el MISMO almacenamiento (CookieStorage) y bajo el
 * MISMO esquema de claves que usa `DefaultTokenStore` de `@aws-amplify/auth`:
 *
 *   CognitoIdentityServiceProvider.<clientId>.LastAuthUser            = <username>
 *   CognitoIdentityServiceProvider.<clientId>.<username>.idToken      = <jwt>
 *   CognitoIdentityServiceProvider.<clientId>.<username>.accessToken  = <jwt>
 *   CognitoIdentityServiceProvider.<clientId>.<username>.refreshToken = <opaque>
 *   CognitoIdentityServiceProvider.<clientId>.<username>.clockDrift   = 0
 *
 * Tras escribirlos, un reload (hard redirect) deja que `AuthProvider` bootstree
 * la sesión desde las cookies (`getCurrentUser` / `fetchAuthSession`), igual que
 * un login normal. El `username` que usa Amplify como clave es el claim
 * `cognito:username` del idToken (fallback: `sub`).
 *
 * La config de CookieStorage debe ser IDÉNTICA a la de AuthContext.js, o Amplify
 * leería cookies distintas a las que escribimos.
 */

import { CookieStorage } from 'aws-amplify/utils';
import awsConfig from '../aws-config';

// Prefijo canónico de las claves de Cognito (constante AUTH_KEY_PREFIX de Amplify).
const AUTH_KEY_PREFIX = 'CognitoIdentityServiceProvider';

/** Decodifica el payload de un JWT (base64url) sin verificar la firma. */
function decodeJwtPayload(jwt) {
  const segment = String(jwt).split('.')[1];
  if (!segment) throw new Error('idToken malformado');
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(json);
}

/** CookieStorage con la MISMA configuración que AuthContext.js. */
function buildCookieStorage() {
  return new CookieStorage({
    domain: window.location.hostname,
    secure: window.location.protocol === 'https:',
    path: '/resto/admin/',
    sameSite: 'strict',
    expires: 30,
  });
}

/**
 * Persiste los tokens Cognito devueltos por complete-signup en el store de
 * Amplify. NO establece la sesión en memoria: el caller debe hacer un reload
 * (hard redirect) para que Amplify la levante desde las cookies.
 *
 * @param {{idToken:string, accessToken:string, refreshToken?:string}} tokens
 */
export async function persistCognitoSession(tokens) {
  const { idToken, accessToken, refreshToken } = tokens || {};
  if (!idToken || !accessToken) {
    throw new Error('complete-signup no devolvió idToken/accessToken');
  }

  const clientId = awsConfig.Auth.Cognito.userPoolClientId;
  const payload = decodeJwtPayload(idToken);
  const username = payload['cognito:username'] || payload.sub;
  if (!username) throw new Error('idToken sin cognito:username/sub');

  const storage = buildCookieStorage();
  const base = `${AUTH_KEY_PREFIX}.${clientId}`;
  const userBase = `${base}.${username}`;

  await storage.setItem(`${base}.LastAuthUser`, username);
  await storage.setItem(`${userBase}.idToken`, idToken);
  await storage.setItem(`${userBase}.accessToken`, accessToken);
  if (refreshToken) {
    await storage.setItem(`${userBase}.refreshToken`, refreshToken);
  }
  await storage.setItem(`${userBase}.clockDrift`, '0');
}

/**
 * URL absoluta a la raíz del panel admin, respetando el basename `/resto/admin`.
 * Se usa para el hard redirect post-sesión (fuerza el bootstrap de Amplify).
 */
export function adminRootUrl() {
  const base = window.location.pathname.startsWith('/resto/admin') ? '/resto/admin' : '';
  return `${window.location.origin}${base}/`;
}
